import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";

// Eagerly load the landing page for best FCP
import Landing from "./pages/Landing";

// Lazy load all other routes to reduce initial bundle
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const FAQ = lazy(() => import("./pages/FAQ"));
const TermsAndPrivacy = lazy(() => import("./pages/TermsAndPrivacy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Banned = lazy(() => import("./pages/Banned"));
const Admin = lazy(() => import("./pages/Admin"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Users = lazy(() => import("./pages/admin/Users"));
const Bans = lazy(() => import("./pages/admin/Bans"));
const Feedback = lazy(() => import("./pages/admin/Feedback"));
const Logs = lazy(() => import("./pages/admin/Logs"));
const Influencers = lazy(() => import("./pages/admin/Influencers"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const InfluencerDashboard = lazy(() => import("./pages/InfluencerDashboard"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/chat" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/termos" element={<TermsAndPrivacy />} />
              <Route path="/banned" element={<Banned />} />
              <Route path="/teste-gratis" element={<Navigate to="/" replace />} />
              <Route path="/ref/:code" element={<ReferralRedirect />} />
              <Route path="/influencer" element={<InfluencerDashboard />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/admin" element={<Admin />}>
                <Route index element={<Dashboard />} />
                <Route path="users" element={<Users />} />
                <Route path="bans" element={<Bans />} />
                <Route path="feedback" element={<Feedback />} />
                <Route path="logs" element={<Logs />} />
                <Route path="influencers" element={<Influencers />} />
              </Route>
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
